import { useEffect, useMemo, useState, type JSX } from "react";
import type { WorkflowTabId } from "@/types/models";
import { getWorkflowAccess } from "@/services/auth/role-privileges";
import { useAuth } from "@/services/auth/use-auth";
import { TabStrip, WORKFLOW_TABS, workflowPanelId, workflowTabId } from "./TabStrip";
import { QuickActions } from "./QuickActions";
import { Deals } from "./Deals";
import { Tasks } from "./Tasks";
import { Profile } from "./Profile";
import "./Workflow.scss";

export function Workflow(): JSX.Element {
  const { user } = useAuth();
  const [tab, setTab] = useState<WorkflowTabId>("quick-actions");
  const access = getWorkflowAccess(user);
  const tabs = useMemo(
    () =>
      WORKFLOW_TABS.filter(
        (candidate) =>
          (candidate.id !== "deals" || access.canViewDeals) && (candidate.id !== "tasks" || access.canViewTasks)
      ),
    [access.canViewDeals, access.canViewTasks]
  );

  useEffect(() => {
    if (!tabs.some((candidate) => candidate.id === tab)) setTab("quick-actions");
  }, [tab, tabs]);

  const content =
    tab === "quick-actions" ? (
      <QuickActions />
    ) : tab === "deals" ? (
      <Deals />
    ) : tab === "tasks" ? (
      <Tasks />
    ) : (
      <Profile />
    );

  return (
    <div className="workflow">
      <div className="workflow__shell">
        <div className="workflow__tabs">
          <TabStrip active={tab} onChange={setTab} tabs={tabs} />
        </div>

        <div className="workflow__content">
          <div
            className="workflow__panel"
            id={workflowPanelId(tab)}
            role="tabpanel"
            aria-labelledby={workflowTabId(tab)}
            tabIndex={0}
          >
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}
